import { useContext } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { ANALYTICS_FLAGS } from "Analytics/flags";
import { AnalyticsEvent } from "Tracking";
import { AnalyticsContext } from "Tracking/AnalyticsProvider";
import { Group, Namespace, Translator, useTranslate } from "localization";
import { Tab } from "minimum-ui/core/types";
import { Entity, FeatureFlag } from "types/Entity";

import { userSelectors } from "store/user/userSelectors";

import { HUB_FEATURE_FLAG } from "utilities/consts";
import {
  Navlink,
  useNavigationLinks,
} from "utilities/hooks/useNavigationLinks";
import urls from "utilities/urls";

export interface UseNavigation {
  activeIndex: number;
  currentOrg: Entity | null;
  email?: string;
  isHubFeatureFlagEnabled: boolean;
  links: Tab[];
  name?: string;
  userOrganisations: ReturnType<typeof userSelectors.organisations>;
  onChange: (nextIndex: number) => void;
  navigate: (to: string) => void;
}

export const useNavigation = (): UseNavigation => {
  const { translate } = useTranslate(Namespace.DEFAULT, Group.TABS);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { trackEvent } = useContext(AnalyticsContext);
  const flags = useSelector(userSelectors.featureFlags);
  const currentOrg = useSelector(userSelectors.currentOrganisation);
  const userOrganisations = useSelector(userSelectors.organisations);
  const name = useSelector(userSelectors.name);
  const email = useSelector(userSelectors.email);

  const links = useNavigationLinks(buildLinks(translate), flags);
  const isHubFeatureFlagEnabled = Boolean(flags[HUB_FEATURE_FLAG]);

  const handleNavClick = (destination: string, origin: string) => {
    trackEvent(AnalyticsEvent.NAVIGATION_BAR_TAB_VIEWED, {
      "Destination Navigation Bar Tab": destination,
      "Origin Navigation Bar Tab": origin,
    });
  };

  const activeIndex = links.findIndex((tab) => {
    if (tab.parentUrl) {
      return pathname.startsWith(tab.parentUrl);
    }

    return pathname.startsWith(tab.url);
  });

  const onChange = (nextIndex: number) => {
    const activeTab = links[activeIndex];
    const nextTab = links[nextIndex];

    if (!nextTab) {
      return;
    }

    handleNavClick(nextTab.label, activeTab?.label || "External");
  };

  return {
    activeIndex,
    currentOrg,
    email,
    isHubFeatureFlagEnabled,
    links,
    name,
    userOrganisations,
    navigate,
    onChange,
  };
};

export const buildLinks = (translate: Translator): Navlink[] => [
  {
    label: translate("home"),
    getUrl: urls.home,
  },
  {
    label: translate("atlas"),
    getUrl: urls.atlas,
    flags: [FeatureFlag.ATLAS_VIEW],
  },
  {
    label: translate("dataSubmission"),
    getUrl: urls.dataSubmissions,
    flags: [FeatureFlag.DATA_COLLECTION_VIEW],
  },
  {
    label: translate("dataSubmissionLegacy"),
    getUrl: urls.legacySubmissions,
    flags: [FeatureFlag.LEGACY_SUBMISSIONS_VIEW],
  },
  {
    label: translate("dataSubmissionLegacy"),
    getUrl: urls.dataSubmission,
    flags: [FeatureFlag.SUBMISSIONS_VIEW],
  },
  {
    label: translate("projectManagement"),
    getUrl: urls.dataTracker,
    flags: [FeatureFlag.DATA_TRACKER_VIEW],
  },
  {
    label: translate("collectionStatus"),
    getUrl: urls.manualSubmission,
    parentUrl: urls.collectionStatus,
    flags: [FeatureFlag.COLLECTION_STATUS_VIEW],
  },
  {
    label: translate("emissions"),
    getUrl: urls.emissionsEmissionsData,
    parentUrl: urls.emissions,
    flags: [FeatureFlag.EMISSIONS_VIEW],
  },
  {
    label: translate("analytics"),
    getUrl: urls.analytics,
    flags: ANALYTICS_FLAGS,
  },
  {
    label: translate("reports"),
    getUrl: urls.requestReport,
    flags: [FeatureFlag.REQUEST_REPORT_VIEW],
  },
  {
    label: translate("documents"),
    getUrl: urls.documents,
    flags: [FeatureFlag.DOCUMENTS_VIEW],
  },
  {
    label: translate("transactions"),
    getUrl: urls.transactions,
    flags: [FeatureFlag.TRANSACTIONS_VIEW],
  },
  {
    label: translate("methodologies"),
    getUrl: urls.methodologies,
    flags: [FeatureFlag.METHODOLOGIES_LIST_VIEW],
  },
  {
    label: translate("emissions"),
    getUrl: urls.newEmissionsData,
    flags: [FeatureFlag.EMISSIONS_BETA_VIEW],
    badgeValue: "BETA",
  },
];
